import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
    CognitoUser,
    UserAttributes,
    UsersResponse,
    UserEventsQuery,
    UserEventsResponse,
    UsageReportsQuery,
    UsageReportsResponse
} from '../model/user-admin.model';

@Injectable({
    providedIn: 'root'
})
export class UserAdminService {
    private usersUrl: string = environment.serverUrl + '/user-data';
    private eventsUrl: string = environment.serverUrl + '/user-events';
    private reportsUrl: string = environment.serverUrl + '/reports';

    constructor(private http: HttpClient) {}

    getUsers(): Observable<UsersResponse> {
        return this.http.get<CognitoUser[]>(this.usersUrl).pipe(
            map(users => ({ users: users || [] })),
            catchError(error => throwError(() => error))
        );
    }

    createUser(username: string, userAttributes: UserAttributes): Observable<unknown> {
        return this.http.post(this.usersUrl, {
            action: 'CREATE',
            username,
            userAttributes
        }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    updateUser(username: string, userAttributes: UserAttributes): Observable<unknown> {
        return this.http.post(this.usersUrl, {
            action: 'UPDATE',
            username,
            userAttributes
        }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    enableUser(username: string): Observable<unknown> {
        return this.http.post(this.usersUrl, { action: 'ENABLE', username }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    disableUser(username: string): Observable<unknown> {
        return this.http.post(this.usersUrl, { action: 'DISABLE', username }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    resendPassword(username: string): Observable<unknown> {
        return this.http.post(this.usersUrl, { action: 'RESEND', username }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    getUserEvents(query: UserEventsQuery = {}): Observable<UserEventsResponse> {
        let params = new HttpParams();
        if (query.username) {
            params = params.append('username', query.username);
        }
        if (query.start_date) {
            params = params.append('start_date', query.start_date);
        }
        if (query.end_date) {
            params = params.append('end_date', query.end_date);
        }
        if (query.action) {
            params = params.append('action', query.action);
        }
        if (query.last_evaluated_key) {
            params = params.append('last_evaluated_key', JSON.stringify(query.last_evaluated_key));
        }
        return this.http.get<UserEventsResponse>(this.eventsUrl, { params }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    getReports(query: UsageReportsQuery = {}): Observable<UsageReportsResponse> {
        let params = new HttpParams();
        if (query.organization) {
            params = params.append('organization', query.organization);
        }
        if (query.start_date) {
            params = params.append('start_date', query.start_date);
        }
        if (query.end_date) {
            params = params.append('end_date', query.end_date);
        }
        if (query.last_evaluated_key) {
            params = params.append('last_evaluated_key', JSON.stringify(query.last_evaluated_key));
        }
        return this.http.get<UsageReportsResponse>(this.reportsUrl, { params }).pipe(
            catchError(error => throwError(() => error))
        );
    }

}
